/**
 * Customers list: everyone who signed up through the partner's link or code,
 * names masked. Search and the status filter run in the browser; a row opens
 * `/customers/:id`.
 */
import { PageHeader } from "@nt/ui/page-header";
import { buttonVariants, inputVariants } from "@nt/ui/recipes";
import { EmptyState } from "@nt/ui/states";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableStack,
  TableStackField,
  TableStackFields,
  TableStackRow,
  TableStackTitle,
} from "@nt/ui/table";
import { Search, Users } from "lucide-react";
import { useState, type MouseEvent } from "react";
import { Link, useNavigate } from "react-router";

import { QueryView, type QueryLike } from "@/components/query-view";
import { Section } from "@/components/section";
import { Segmented } from "@/components/segmented";
import { StatusChip } from "@/components/status-chip";
import { money, shortDate, countryName } from "@/lib/format";
import { useGet } from "@/lib/queries";
import type { CustomerRow, CustomersData } from "@/lib/types";

import { filterCustomers, STATUS_FILTERS, type StatusFilter } from "./filter";
import { customerPath, edgeToEdge, sourceLabel } from "./shared";

/**
 * A row click opens the detail page, except when the click landed on the
 * name link (it navigates itself) or the status chip (it shows a tooltip).
 */
function useRowClick() {
  const navigate = useNavigate();
  return (id: string) => (event: MouseEvent<HTMLElement>) => {
    const target = event.target as Element | null;
    if (target?.closest("a, button")) return;
    void navigate(customerPath(id));
  };
}

function CustomerTable({ rows }: { rows: CustomerRow[] }) {
  const onRow = useRowClick();
  return (
    <>
      <div className={`hidden sm:block ${edgeToEdge}`}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Signed up</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>First order</TableHead>
              <TableHead className="text-right">Fee</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.id}
                onClick={onRow(r.id)}
                className="cursor-pointer"
              >
                <TableCell className="font-medium">
                  <Link
                    to={customerPath(r.id)}
                    className="rounded-sm text-foreground outline-none hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    {r.name}
                  </Link>
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {shortDate(r.signedUpAt)}
                </TableCell>
                <TableCell className="normal-nums">{sourceLabel(r)}</TableCell>
                <TableCell>
                  {r.country ? countryName(r.country) : "Unknown"}
                </TableCell>
                <TableCell className="normal-nums">
                  {r.product ?? "No order yet"}
                </TableCell>
                <TableCell className="text-right">
                  {r.feeCents === null ? "" : money(r.feeCents)}
                </TableCell>
                <TableCell>
                  <StatusChip status={r.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TableStack className="sm:hidden">
        {rows.map((r) => (
          <TableStackRow
            key={r.id}
            onClick={onRow(r.id)}
            className="cursor-pointer px-(--card-spacing)"
          >
            <TableStackTitle>
              <Link
                to={customerPath(r.id)}
                className="min-w-0 truncate rounded-sm outline-none hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                {r.name}
              </Link>
              <StatusChip status={r.status} />
            </TableStackTitle>
            <TableStackFields>
              <TableStackField label="Signed up">
                {shortDate(r.signedUpAt)}
              </TableStackField>
              <TableStackField label="Source">{sourceLabel(r)}</TableStackField>
              <TableStackField label="First order">
                {r.product ?? "No order yet"}
              </TableStackField>
              <TableStackField label="Fee">
                {r.feeCents === null ? "None yet" : money(r.feeCents)}
              </TableStackField>
            </TableStackFields>
          </TableStackRow>
        ))}
      </TableStack>
    </>
  );
}

export function CustomersView({
  data,
  initialSearch = "",
  initialStatus = "all",
}: {
  data: CustomersData;
  initialSearch?: string;
  initialStatus?: StatusFilter;
}) {
  const [search, setSearch] = useState(initialSearch);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);

  if (data.customers.length === 0) {
    return (
      <Section title="Your customers">
        <EmptyState
          icon={<Users />}
          title="No customers yet"
          description="When someone signs up through your link or code, they show up here with their name masked. Share your link to get started."
          action={
            <Link to="/links" className={buttonVariants()}>
              Get your link
            </Link>
          }
        />
      </Section>
    );
  }

  const rows = filterCustomers(data.customers, { search, status });
  const listed = data.customers.length;
  const cut = data.truncated;
  return (
    <Section
      title="Your customers"
      description={
        rows.length !== listed
          ? `${rows.length} of ${listed} ${cut ? "newest " : ""}customers`
          : cut
            ? `Newest ${listed} of ${data.total} customers`
            : `${listed} ${listed === 1 ? "customer" : "customers"}`
      }
      contentClassName="px-0"
    >
      <div className="flex flex-col gap-3 px-(--card-spacing) pb-3 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-xs">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            aria-label="Search customers"
            placeholder="Search by name or product"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={inputVariants({ className: "pl-9" })}
          />
        </div>
        <Segmented
          aria-label="Filter by status"
          size="sm"
          value={status}
          onChange={setStatus}
          options={STATUS_FILTERS}
        />
      </div>
      {rows.length === 0 ? (
        <EmptyState
          title="No customers match"
          description="Try another name or product, or show every status."
          action={
            <button
              type="button"
              className={buttonVariants({ variant: "secondary" })}
              onClick={() => {
                setSearch("");
                setStatus("all");
              }}
            >
              Clear filters
            </button>
          }
        />
      ) : (
        <CustomerTable rows={rows} />
      )}
      <p className="mt-3 px-(--card-spacing) text-compact text-muted-foreground">
        Names are masked to protect customers. Hover or focus a status to see
        what it means.
        {cut
          ? ` Search and filters cover these ${listed}. To find someone older, write to support@nonresident.tax.`
          : null}
      </p>
    </Section>
  );
}

export function CustomersScreen({
  query,
}: {
  query: QueryLike<CustomersData>;
}) {
  return (
    <QueryView query={query} what="your customers" rows={6}>
      {(data) => <CustomersView data={data} />}
    </QueryView>
  );
}

export function Customers() {
  const query = useGet<CustomersData>("customers", "/api/partner/customers");
  return (
    <>
      <PageHeader
        title="Customers"
        description="People who signed up through your link or code, and what you earned from each."
      />
      <CustomersScreen query={query} />
    </>
  );
}
